<template>
  <div class="heatmap-view-container">
    <AppTopBar :show-admin="false" :show-logout="false" />

    <div v-if="error" class="error-message"><PhWarning :size="14" weight="bold" /> {{ error }}</div>

    <div v-if="!error" class="hv-content">
      <div class="hv-header-bar">
        <div class="hv-title-block">
          <span class="hv-kicker">HEATMAP</span>
          <h1 class="hv-title">
            {{ heatmapName }}
            <span v-if="heatmapVista" class="vista-tag" :class="heatmapVista">
              {{ heatmapVista === 'web' ? 'WEB PROPIA' : 'MARKETPLACE' }}
            </span>
          </h1>
          <span class="hv-meta">{{ productCodes.length }} productos · datos al día</span>
        </div>

        <div class="hv-right-controls">
          <!-- Mute button -->
          <button @click="toggleMute" class="mute-btn" :class="{ muted: muted }"
            :title="muted ? 'Activar alertas de sonido' : 'Silenciar alertas'">
            {{ muted ? '🔇' : '🔔' }}
          </button>

          <!-- Scrap navigator -->
          <div class="date-navigator" v-if="!loading">
            <button @click="navigateScrap('prev')" class="nav-btn" :disabled="isPrevDisabled" title="Scrap anterior">‹</button>
            <div class="current-date" :class="{ 'is-latest': isLatest }" :title="scrapTooltip">
              <span class="date-label">SCRAP</span>
              <span class="date-value">{{ formattedDate }}</span>
            </div>
            <button @click="navigateScrap('next')" class="nav-btn" :disabled="isNextDisabled" title="Scrap siguiente">›</button>
          </div>
        </div>
      </div>

      <div v-if="loading" class="loading-message">
        <div class="spinner"></div>
        <span>Cargando datos del Heatmap...</span>
      </div>

      <div class="heatmap-wrapper" v-if="!loading && productCodes.length > 0">
        <HeatmapPrecios
          v-if="heatmapData.length > 0"
          :heatmap-data="heatmapData"
          :cambios="cambios"
          :class="{ 'is-loading': isNavigating }"
          :key="currentScrapUuid"
        />

        <div v-if="!isNavigating && heatmapData.length === 0" class="no-data-message">
          No se encontraron datos de precios para esta fecha.
        </div>

        <div v-if="isNavigating" class="navigation-overlay">
          <div class="spinner"></div>
          <p>Cargando datos...</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue';
import { useRoute } from 'vue-router';
import axios from 'axios';
import HeatmapPrecios from '../components/HeatmapPrecios.vue';
import AppTopBar from '../components/AppTopBar.vue';
import { PhWarning } from '@phosphor-icons/vue'

const getAuthHeaders = () => {
  const token = localStorage.getItem('token_precios');
  return { headers: { Authorization: `Bearer ${token}` } };
};

const route = useRoute();
const heatmapName = ref('');
const heatmapVista = ref('web');
const productCodes = ref([]);
const heatmapData = ref([]);
const loading = ref(true);
const isNavigating = ref(false);
const error = ref(null);
const currentScrapUuid = ref(null);
const scrapInfo = ref(null);
const navegacion = ref({ anterior_uuid: null, siguiente_uuid: null });
const cambios = ref({});
const muted = ref(localStorage.getItem('heatmap_muted') === 'true');
const pollingTimer = ref(null);

// ============ SONIDO ============
const playAlertSound = () => {
  if (muted.value) return;
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.setValueAtTime(660, ctx.currentTime + 0.15);
    gain.gain.setValueAtTime(0.25, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.6);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.6);
  } catch(e) { /* audio no soportado */ }
};

const toggleMute = () => {
  muted.value = !muted.value;
  localStorage.setItem('heatmap_muted', String(muted.value));
};

// ============ DETECCIÓN DE CAMBIOS ============
const detectChanges = (oldProducts, newProducts) => {
  const result = {};
  if (!oldProducts?.length || !newProducts?.length) return result;
  const oldMap = {};
  oldProducts.forEach(p => { oldMap[p.codigo] = p; });
  newProducts.forEach(newP => {
    const oldP = oldMap[newP.codigo];
    if (!oldP) return;
    Object.keys(newP.precios).forEach(comp => {
      const key = `${newP.codigo}__${comp}`;
      const oldVal = oldP.precios[comp];
      const newVal = newP.precios[comp];
      if (oldVal === null && newVal !== null) {
        result[key] = { tipo: 'appeared', curr: newVal };
      } else if (oldVal !== null && newVal === null) {
        result[key] = { tipo: 'disappeared', prev: oldVal };
      } else if (oldVal !== null && newVal !== null && Math.abs(oldVal - newVal) > 0.01) {
        result[key] = { tipo: newVal > oldVal ? 'up' : 'down', prev: oldVal, curr: newVal };
      }
    });
  });
  return result;
};

// ============ FORMATO ============
const formatPYTime = (isoString) => {
  if (!isoString) return '—';
  const d = new Date(isoString + (isoString.endsWith('Z') ? '' : 'Z'));
  const opts = { timeZone: 'America/Asuncion', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false };
  return d.toLocaleString('es-PY', opts).replace(',', '');
};

const ETIQUETA_SCRAP = {
  full: { corta: 'full', larga: 'Scrap completo' },
  parcial: { corta: 'parcial', larga: 'Scrap parcial' },
  heatmap: { corta: 'heatmap', larga: 'Scrap del heatmap' },
};

const formattedDate = computed(() => {
  if (!scrapInfo.value?.hora_scrap) return '—';
  const tipo = ETIQUETA_SCRAP[scrapInfo.value.tipo] || ETIQUETA_SCRAP.heatmap;
  return `${formatPYTime(scrapInfo.value.hora_scrap)} (${tipo.corta})`;
});

const scrapTooltip = computed(() => {
  if (!scrapInfo.value) return '';
  const tipo = ETIQUETA_SCRAP[scrapInfo.value.tipo] || ETIQUETA_SCRAP.heatmap;
  return `${tipo.larga} - ${scrapInfo.value.filas_totales} filas`;
});

const isPrevDisabled = computed(() => !navegacion.value.anterior_uuid);
const isNextDisabled = computed(() => !navegacion.value.siguiente_uuid);
const isLatest = computed(() => !navegacion.value.siguiente_uuid && !!scrapInfo.value);

// ============ NAVEGACIÓN ============
const navigateScrap = (direction) => {
  const target = direction === 'prev'
    ? navegacion.value.anterior_uuid
    : navegacion.value.siguiente_uuid;
  if (!target) return;
  currentScrapUuid.value = target;
  fetchHeatmapData(target);
};

const API_BASE_URL = 'https://dash-visaovip.kompara.tech/api';

// ============ FETCH (con detección de cambios en navegación) ============
const fetchHeatmapData = async (scrapUuid = null) => {
  if (productCodes.value.length === 0) return;
  isNavigating.value = true;
  const oldData = heatmapData.value.length > 0
    ? heatmapData.value.map(p => ({ ...p, precios: { ...p.precios } }))
    : null;
  try {
    const url = `${API_BASE_URL}/heatmap/dinamico?vista=${heatmapVista.value}`;
    const body = { codigos: productCodes.value };
    if (scrapUuid) body.scrap_uuid = scrapUuid;
    const response = await axios.post(url, body, getAuthHeaders());
    const newData = response.data.productos || [];
    heatmapData.value = newData;
    scrapInfo.value = response.data.scrap_info;
    navegacion.value = response.data.navegacion || { anterior_uuid: null, siguiente_uuid: null };
    if (scrapInfo.value) currentScrapUuid.value = scrapInfo.value.scrap_uuid;
    // Detectar cambios solo al navegar (no en la carga inicial)
    cambios.value = (oldData && scrapUuid) ? detectChanges(oldData, newData) : {};
  } catch (err) {
    console.error("Error al generar el heatmap:", err);
    error.value = "No se pudieron cargar los datos del scrap.";
  } finally {
    isNavigating.value = false;
    loading.value = false;
  }
};

// ============ AUTO-REFRESH (silencioso, solo cuando es el último scrap) ============
const checkForNewScrap = async () => {
  if (!isLatest.value || productCodes.value.length === 0) return;
  try {
    const url = `${API_BASE_URL}/heatmap/dinamico?vista=${heatmapVista.value}`;
    const response = await axios.post(url, { codigos: productCodes.value }, getAuthHeaders());
    const newScrapInfo = response.data.scrap_info;
    if (!newScrapInfo || newScrapInfo.scrap_uuid === currentScrapUuid.value) return;
    // Nuevo scrap detectado
    const newData = response.data.productos || [];
    const detected = detectChanges(heatmapData.value, newData);
    cambios.value = detected;
    heatmapData.value = newData;
    scrapInfo.value = newScrapInfo;
    navegacion.value = response.data.navegacion || { anterior_uuid: null, siguiente_uuid: null };
    currentScrapUuid.value = newScrapInfo.scrap_uuid;
    if (Object.keys(detected).length > 0) playAlertSound();
  } catch(e) {
    console.error('Auto-refresh error:', e);
  }
};

const startPolling = () => {
  pollingTimer.value = setInterval(checkForNewScrap, 2 * 60 * 1000);
};

onMounted(async () => {
  const heatmapId = route.params.id;
  if (!heatmapId) {
    error.value = "ID de Heatmap no especificado.";
    loading.value = false;
    return;
  }
  try {
    const savedHeatmapResponse = await axios.get(`${API_BASE_URL}/saved-heatmaps/${heatmapId}`, getAuthHeaders());
    heatmapName.value = savedHeatmapResponse.data.name;
    heatmapVista.value = savedHeatmapResponse.data.vista || 'web';
    productCodes.value = savedHeatmapResponse.data.product_codes;
    if (!productCodes.value || productCodes.value.length === 0) {
      error.value = "Esta vista no tiene productos.";
      loading.value = false;
      return;
    }
    await fetchHeatmapData();
    startPolling();
  } catch (err) {
    console.error("Error al cargar la vista del heatmap:", err);
    error.value = "No se pudo cargar la vista del heatmap.";
    loading.value = false;
  }
});

onUnmounted(() => {
  if (pollingTimer.value) clearInterval(pollingTimer.value);
});
</script>

<style scoped>
.heatmap-view-container {
  background: var(--color-background);
  color: var(--color-text);
  min-height: 100vh;
}
.hv-content {
  padding: 20px 24px;
  max-width: 100%;
  margin: 0 auto;
}

/* ============ HEADER BAR ============ */
.hv-header-bar {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 20px;
  padding: 4px 0 14px;
  border-bottom: 1px solid var(--color-border);
  margin-bottom: 20px;
  flex-wrap: wrap;
}
.hv-title-block {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
}
.hv-kicker {
  font-family: var(--font-mono);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.15em;
  color: var(--color-accent);
  text-transform: uppercase;
}
.hv-title {
  margin: 0;
  font-family: 'Inter', sans-serif;
  font-size: 20px;
  font-weight: 700;
  color: var(--color-text);
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  letter-spacing: -0.01em;
}
.hv-meta {
  font-family: var(--font-mono);
  font-size: 10.5px;
  color: var(--color-text-muted);
  letter-spacing: 0.03em;
}
.vista-tag {
  display: inline-block;
  font-family: var(--font-mono);
  font-size: 9.5px;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 2px;
  letter-spacing: 0.1em;
  border: 1px solid;
}
.vista-tag.web {
  background: rgba(56, 189, 248, 0.10);
  color: var(--color-min-price-text);
  border-color: rgba(56, 189, 248, 0.35);
}
.vista-tag.marketplace {
  background: var(--color-leader-bg);
  color: var(--color-leader-text);
  border-color: var(--color-leader);
}

/* ============ RIGHT CONTROLS (mute + navigator) ============ */
.hv-right-controls {
  display: flex;
  align-items: center;
  gap: 8px;
}

.mute-btn {
  background: var(--color-surface);
  border: 1px solid var(--color-border-strong);
  border-radius: 3px;
  width: 34px;
  height: 36px;
  font-size: 15px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
  opacity: 0.85;
}
.mute-btn:hover { opacity: 1; border-color: var(--color-accent); }
.mute-btn.muted { opacity: 0.45; }

/* ============ DATE NAVIGATOR ============ */
.date-navigator {
  display: flex;
  align-items: center;
  gap: 4px;
  background: var(--color-surface);
  border: 1px solid var(--color-border-strong);
  padding: 3px;
  border-radius: 3px;
}
.nav-btn {
  background: transparent;
  border: 1px solid transparent;
  color: var(--color-text-muted);
  font-size: 16px;
  font-weight: 700;
  line-height: 1;
  width: 30px;
  height: 30px;
  cursor: pointer;
  border-radius: 2px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
  font-family: var(--font-mono);
}
.nav-btn:hover:not(:disabled) {
  color: var(--color-accent);
  background: var(--color-accent-bg);
  border-color: var(--color-accent-border);
}
.nav-btn:disabled { color: var(--color-text-faint); cursor: not-allowed; opacity: 0.4; }
.current-date {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1px;
  padding: 2px 14px;
  min-width: 180px;
}
.date-label {
  font-family: var(--font-mono);
  font-size: 8.5px;
  font-weight: 700;
  letter-spacing: 0.12em;
  color: var(--color-text-faint);
  text-transform: uppercase;
}
.date-value {
  font-family: var(--font-mono);
  font-size: 12px;
  font-weight: 600;
  color: var(--color-text);
  font-variant-numeric: tabular-nums;
}
.current-date.is-latest {
  border-color: #16a34a !important;
  box-shadow: 0 0 0 1px #16a34a;
}
.current-date.is-latest .date-label { color: #16a34a; }

/* ============ MENSAJES ============ */
.loading-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 60px 20px;
  font-family: var(--font-mono);
  font-size: 12px;
  color: var(--color-text-muted);
  letter-spacing: 0.05em;
  text-transform: uppercase;
}
.error-message {
  margin: 40px auto;
  max-width: 600px;
  padding: 14px 20px;
  background: var(--color-danger-bg);
  border: 1px solid var(--color-danger);
  color: var(--color-danger-text);
  border-radius: 3px;
  font-family: var(--font-mono);
  font-size: 12px;
  text-align: center;
}
.no-data-message {
  margin: 40px auto;
  max-width: 500px;
  padding: 18px;
  background: var(--color-surface-2);
  border: 1px dashed var(--color-border-strong);
  color: var(--color-text-muted);
  border-radius: 3px;
  font-family: var(--font-mono);
  font-size: 12px;
  text-align: center;
  font-style: italic;
  letter-spacing: 0.03em;
}

/* ============ HEATMAP WRAPPER ============ */
.heatmap-wrapper {
  position: relative;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 3px;
  padding: 12px;
  overflow: hidden;
}
.heatmap-wrapper .is-loading {
  filter: blur(2px) brightness(0.8);
  transition: filter 0.2s ease-in-out;
}
.navigation-overlay {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(2px);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 10px;
  z-index: 10;
  border-radius: 2px;
}
.navigation-overlay p {
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--color-text);
  margin: 0;
}

/* ============ SPINNER ============ */
.spinner {
  border: 3px solid var(--color-border-strong);
  border-top-color: var(--color-accent);
  border-radius: 50%;
  width: 32px;
  height: 32px;
  animation: spin 0.8s linear infinite;
}
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>
